import React from 'react';
import {
  Box, Typography, Link, Table, TableBody, TableCell, TableHead, TableRow, Paper,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * Tiny dependency-free Markdown renderer for our long-form content (tool bodies,
 * blog posts, glossary, About/Contact/Help). Deliberately covers ONLY the subset
 * our authored content uses — headings (#/##/###), paragraphs, ordered &
 * unordered lists, GFM tables, and inline **bold**, *italic*, `code`, and
 * [links](href) — so it stays small and prerenders cleanly under react-snap
 * (no ESM/CRA interop risk from a third-party markdown lib).
 *
 * Internal links (a leading "/" or an absolute https://pdf-edit.tech URL) render
 * as crawlable RouterLink anchors; everything else opens in a new tab.
 *
 * `headingShift` bumps every heading level (e.g. 1 → a leading "#" renders as an
 * <h2>) so an embedded body never introduces a second <h1> on a tool page.
 */

const SITE = 'https://pdf-edit.tech';

function normalizeHref(href) {
  if (href.startsWith(SITE)) {
    const path = href.slice(SITE.length) || '/';
    return { href: path.startsWith('/') ? path : `/${path}`, internal: true };
  }
  if (href.startsWith('/')) return { href, internal: true };
  return { href, internal: false };
}

// Inline: `code`, **bold**, *italic*, _italic_, [text](href). Bold is listed
// before italic so "**" is consumed as bold, not as two italics.
const INLINE_RE = /`([^`]+)`|\*\*([^*]+)\*\*|\*([^*\n]+)\*|_([^_\n]+)_|\[([^\]]+)\]\(([^)]+)\)/g;

function renderInline(text, keyBase) {
  const nodes = [];
  let last = 0;
  let m;
  let i = 0;
  INLINE_RE.lastIndex = 0;
  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const key = `${keyBase}-${i++}`;
    if (m[1] != null) {
      nodes.push(
        <Box key={key} component="code" sx={{
          px: 0.6, py: 0.1, mx: 0.2, borderRadius: 1, fontSize: '0.85em',
          fontFamily: 'monospace', background: 'rgba(99,102,241,0.1)',
        }}>{m[1]}</Box>,
      );
    } else if (m[2] != null) {
      nodes.push(<Box key={key} component="strong" sx={{ fontWeight: 700 }}>{m[2]}</Box>);
    } else if (m[3] != null || m[4] != null) {
      nodes.push(<Box key={key} component="em">{m[3] != null ? m[3] : m[4]}</Box>);
    } else if (m[5] != null) {
      const { href, internal } = normalizeHref(m[6]);
      nodes.push(internal
        ? <Link key={key} component={RouterLink} to={href} underline="hover" sx={{ fontWeight: 500 }}>{m[5]}</Link>
        : <Link key={key} href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 500 }}>{m[5]}</Link>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

const HEADING_SX = {
  1: { fontSize: { xs: '1.8rem', md: '2.3rem' }, fontWeight: 800, mt: 0, mb: 2.5, lineHeight: 1.2 },
  2: { fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 700, mt: 4, mb: 2 },
  3: { fontSize: { xs: '1.1rem', md: '1.25rem' }, fontWeight: 700, mt: 3, mb: 1.5 },
  4: { fontSize: '1.05rem', fontWeight: 700, mt: 2.5, mb: 1 },
  5: { fontSize: '1rem', fontWeight: 700, mt: 2, mb: 1 },
  6: { fontSize: '0.95rem', fontWeight: 700, mt: 2, mb: 1 },
};

// Split source into block groups, keeping multi-line list/table blocks together.
function toBlocks(src) {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  const isUl = (l) => /^\s*[-*]\s+/.test(l);
  const isOl = (l) => /^\s*\d+\.\s+/.test(l);
  const isTable = (l) => /^\s*\|.*\|\s*$/.test(l);
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    const heading = line.match(/^(#{1,6})\s+(.*)$/);
    if (heading) { blocks.push({ type: 'heading', level: heading[1].length, text: heading[2].trim() }); i++; continue; }
    if (isTable(line) && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1]) && lines[i + 1].includes('-')) {
      const rows = [];
      while (i < lines.length && isTable(lines[i])) { rows.push(lines[i]); i++; }
      blocks.push({ type: 'table', rows });
      continue;
    }
    if (isUl(line)) {
      const items = [];
      while (i < lines.length && isUl(lines[i])) { items.push(lines[i].replace(/^\s*[-*]\s+/, '')); i++; }
      blocks.push({ type: 'ul', items });
      continue;
    }
    if (isOl(line)) {
      const items = [];
      while (i < lines.length && isOl(lines[i])) { items.push(lines[i].replace(/^\s*\d+\.\s+/, '')); i++; }
      blocks.push({ type: 'ol', items });
      continue;
    }
    const para = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,6})\s+/.test(lines[i]) && !isUl(lines[i]) && !isOl(lines[i]) && !isTable(lines[i])) {
      para.push(lines[i].trim()); i++;
    }
    blocks.push({ type: 'p', text: para.join(' ') });
  }
  return blocks;
}

function parseTable(rows) {
  const cells = (r) => r.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
  return { head: cells(rows[0]), body: rows.slice(2).map(cells) };
}

export default function Markdown({ children, headingShift = 0, sx }) {
  if (!children) return null;
  const blocks = toBlocks(children);
  return (
    <Box sx={sx}>
      {blocks.map((b, idx) => {
        const key = `b-${idx}`;
        if (b.type === 'heading') {
          const level = Math.min(6, Math.max(1, b.level + headingShift));
          return (
            <Typography key={key} component={`h${level}`} sx={HEADING_SX[level]}>
              {renderInline(b.text, key)}
            </Typography>
          );
        }
        if (b.type === 'p') {
          return (
            <Typography key={key} component="p" color="text.secondary" sx={{ mb: 2, lineHeight: 1.75 }}>
              {renderInline(b.text, key)}
            </Typography>
          );
        }
        if (b.type === 'ul' || b.type === 'ol') {
          return (
            <Box key={key} component={b.type} sx={{ pl: 3, mb: 2.5, color: 'text.secondary' }}>
              {b.items.map((it, j) => (
                <Typography key={`${key}-${j}`} component="li" color="text.secondary" sx={{ mb: 0.75, lineHeight: 1.7 }}>
                  {renderInline(it, `${key}-${j}`)}
                </Typography>
              ))}
            </Box>
          );
        }
        if (b.type === 'table') {
          const { head, body } = parseTable(b.rows);
          return (
            <Paper key={key} elevation={0} sx={{ border: '1px solid rgba(99,102,241,0.12)', borderRadius: 3, overflow: 'hidden', mb: 2.5 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: 'rgba(99,102,241,0.05)' }}>
                    {head.map((h, j) => <TableCell key={j} sx={{ fontWeight: 700 }}>{renderInline(h, `${key}-h-${j}`)}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {body.map((row, r) => (
                    <TableRow key={r} hover>
                      {row.map((c, j) => <TableCell key={j}>{renderInline(c, `${key}-${r}-${j}`)}</TableCell>)}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          );
        }
        return null;
      })}
    </Box>
  );
}
